import { createFileRoute, notFound, useLocation } from "@tanstack/react-router";
import { Suspense } from "react";
import { PageRoutePendingShell } from "@/components/entity-tabs/tab-states";
import { PublicShellReadySignal } from "@/components/public-shell-ready";
import {
	splitRecordViewPath,
	viewUrlParams,
} from "@/components/views/views-data";
import { useResolvedPath } from "@/lib/api";
import { useAuthState } from "@/lib/auth-state";
import { EVENT_FILTER_KEYS } from "@/lib/event-filters";
import { lazyWithReload } from "@/lib/lazy-with-reload";
import { PEEK_SEARCH_KEYS } from "@/lib/peek-search";
import { isReservedOwnerSegment } from "@/lib/reserved";
import {
	deriveEntityResolvePath,
	entitySplatFromPathname,
} from "@/lib/resolve-entity-path";
import {
	shouldRedirectAnonymousToSignIn,
	useAnonymousSignInRedirect,
} from "@/lib/sign-in-redirect";

const EntityPage = lazyWithReload(async () => ({
	default: (await import("@/components/entity-page")).EntityPage,
}));

// Search params for entity tabs
// Values are kept as their parsed types (numbers stay numbers) so TanStack Router
// doesn't JSON-encode numeric strings like "172" -> %22172%22. On a record view
// path (`<type>/<slug>/-/views/<key>`) the whole query is that view's params,
// so every scalar passes through; objects/arrays are never valid param values
// and are dropped.
type EntitySearchParams = Record<string, string | number | boolean | undefined>;

/**
 * Page-shaped loading state for the resolve gate. We can't paint the entity's
 * identity chrome (name / breadcrumb / type) yet — the resolve is what tells us
 * which entity this is — so a page-shaped `PageRoutePendingShell` is the
 * honest body skeleton (DESIGN_GUIDELINES §5). `data-public-loading` keeps the SSR public
 * shell up until real content renders (see `lib/public-shell.ts`).
 */
function ResolveLoadingState() {
	return (
		<div data-public-loading="true">
			<PageRoutePendingShell />
		</div>
	);
}

function EntityRoutePendingShell() {
	return (
		<div data-public-loading="true">
			<PageRoutePendingShell />
		</div>
	);
}

export const Route = createFileRoute("/$owner/$")({
	pendingComponent: EntityRoutePendingShell,
	beforeLoad: ({ location }) => {
		const splat = entitySplatFromPathname(location.pathname);
		const firstSegment = splat.split("/")[0];
		if (firstSegment && isReservedOwnerSegment(firstSegment)) {
			throw notFound();
		}
		if (!splitRecordViewPath(splat)) throw notFound();
	},
	component: OwnerEntityRoute,
	validateSearch: (search: Record<string, unknown>): EntitySearchParams => {
		// Every scalar first, so a view path keeps its params; known host keys
		// below overwrite with their typed parsing.
		const result: EntitySearchParams = viewUrlParams(search);

		// Peek keys ride along: "See activity" opens the pane on this very route,
		// so dropping them here would strip the pane the moment it opened.
		const knownParams = [...EVENT_FILTER_KEYS, ...PEEK_SEARCH_KEYS, "page"];

		// Only scalars take the host's typing. An array or object is a view
		// param (the host never writes one to these keys), and viewUrlParams
		// already holds it as its exact URL text; String() would flatten
		// `[1,2]` to `1,2` and an object to `[object Object]`.
		const hostValue = (val: unknown) =>
			typeof val === "number" ? val : String(val);
		const isScalar = (val: unknown) => val != null && typeof val !== "object";

		for (const key of knownParams) {
			if (isScalar(search[key])) result[key] = hostValue(search[key]);
		}

		for (const key of Object.keys(search)) {
			if (key.startsWith("clf_") && isScalar(search[key])) {
				result[key] = String(search[key]);
			}
		}

		return result;
	},
	// Fire-and-forget: warm the resolve for this entity path so it's cached
	// before the component mounts (and on preload-intent hover). Awaits nothing
	// per DESIGN_GUIDELINES — the component paints its skeleton immediately.
	loader: ({ context: { queryClient }, params, location }) => {
		const { path } = deriveEntityResolvePath(
			params.owner,
			splitRecordViewPath(entitySplatFromPathname(location.pathname))
				?.entityPath,
		);
		void queryClient.prefetchQuery(
			// Scope to params.owner so the prefetch key matches the component's read
			// and the scope never falls back to URL parsing (which throws at the bare origin).
			//
			// No `includeBootstrap`: the payload is unread on an entity path, and
			// asking for it split the cache key from the Activity pane's own
			// resolve of the same record — two resolves of one page.
			useResolvedPath.queryOptions(path, { slug: params.owner }),
		);
	},
});

function OwnerEntityRoute() {
	const { owner } = Route.useParams();
	const pathname = useLocation({ select: (location) => location.pathname });
	// beforeLoad already 404'd a malformed suffix, so this split always parses.
	const recordPath = splitRecordViewPath(entitySplatFromPathname(pathname));
	const viewKey = recordPath?.viewKey;
	const { path } = deriveEntityResolvePath(owner, recordPath?.entityPath);

	// Scope the resolve to the route's owner explicitly. Passing `undefined`
	// makes resolveApiScope fall back to URL/subdomain parsing (getPathScope),
	// which THROWS at the bare gateway origin (no subdomain, e.g. the Owletto
	// extension's embedded iframe) — the query then errors before any fetch and
	// the page renders "Not Found". We already know the owner from the route.
	const { data, isLoading, error } = useResolvedPath(path, { slug: owner });
	const { session, isReady: authReady } = useAuthState();

	// Redirect to login if not authenticated and path failed to resolve.
	// Skip the redirect for public orgs — SSR only injects a public bootstrap
	// when the org's visibility is 'public', so the bootstrap's presence on
	// this owner is a sufficient signal to let the page render anonymously.
	useAnonymousSignInRedirect({
		active: shouldRedirectAnonymousToSignIn({
			settled: !isLoading,
			authReady,
			session,
			resolved: !!data,
			error,
		}),
		owner,
	});

	if (isLoading || (!authReady && !data)) {
		return <ResolveLoadingState />;
	}
	// An unresolved entity path is terminal; the workspace root has its own route.
	if (error || !data || !data.entity) {
		return (
			<>
				<PublicShellReadySignal />
				<div className="flex flex-col items-center justify-center py-16 text-center">
					<h1 className="text-2xl font-semibold">Not Found</h1>
					<p className="text-muted-foreground mt-2">
						We couldn't resolve this path.
					</p>
				</div>
			</>
		);
	}

	return (
		<>
			<PublicShellReadySignal />
			<Suspense fallback={<ResolveLoadingState />}>
				<EntityPage
					namespace={data.workspace}
					path={data.path}
					entity={data.entity}
					viewKey={viewKey}
				/>
			</Suspense>
		</>
	);
}
